/**
 * DashboardFinanceTab.jsx — Phase 147 (Dashboard Cleanup), extracted from
 * Dashboard.jsx unchanged (Finance tab body: Studio Runway, Month Selector +
 * MIS export, summary tiles, snapshot tiles, Invoice Ageing).
 */
import React from "react";
import { Button } from "@/components/ui/button";
import InvoiceAgeingWidget from "@/components/InvoiceAgeingWidget";
import MonthSelector from "@/components/MonthSelector";
import { TileLink } from "@/components/dashboard/DashboardCards";
import TabSection from "@/components/dashboard/TabSection";
import { INR } from "@/lib/dashboardHelpers";
import { Fuel, Download, TrendingUp, TrendingDown, FileText, ShoppingCart, Receipt } from "lucide-react";

export default function DashboardFinanceTab({
  activeTab, canFinance, runway, financeMonth, setFinanceMonth,
  financeSummary, outstandingInvoices, pendingPurchases,
}) {
  return (
    <TabSection activeTab={activeTab} id="finance">
      {!canFinance ? (
        <div className="card-flat p-6 text-sm text-slate-500">Finance view is restricted.</div>
      ) : (
        <>
          {/* Phase 93 — Studio Runway: large orange-accented hero widget */}
          {runway && (
            <div
              className={`rounded-sm p-6 mb-6 border-2 ${
                runway.color === "green" ? "bg-green-50 border-green-300" :
                runway.color === "orange" ? "bg-orange-50 border-[#F54800]" : "bg-red-50 border-red-400"
              }`}
              data-testid="studio-runway-widget"
            >
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <Fuel size={28} className={runway.color === "green" ? "text-green-600" : runway.color === "orange" ? "text-[#F54800]" : "text-red-600"} />
                  <div>
                    <div className="label-overline">Studio Runway</div>
                    <div className={`font-display text-4xl sm:text-5xl font-bold ${
                      runway.color === "green" ? "text-green-700" : runway.color === "orange" ? "text-[#F54800]" : "text-red-700"
                    }`} data-testid="studio-runway-months">
                      {runway.cash_entered === false ? "Cash not entered" : runway.runway_months === null ? "∞" : runway.current_cash < 0 ? "Cash negative" : `${Math.max(0, runway.runway_months)} mo`}
                    </div>
                  </div>
                </div>
                <div className="flex gap-6 text-right">
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">Current cash</div>
                    <div className="font-mono text-sm font-semibold text-slate-800" data-testid="studio-runway-current-cash">{runway.cash_entered === false ? "Not entered" : INR(runway.current_cash)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">+ Expected (Sent)</div>
                    <div className="font-mono text-sm font-semibold text-slate-800">{INR(runway.expected_from_sent)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">÷ Avg burn/mo</div>
                    <div className="font-mono text-sm font-semibold text-slate-800">{INR(runway.avg_monthly_burn)}</div>
                  </div>
                </div>
              </div>
              <div className="text-[10px] text-slate-500 mt-3">
                (Current Cash + Expected Revenue from Sent Invoices) ÷ Average Monthly Burn · Current Cash = sum of active bank-account Statement Balances (same as Total Studio Cash)
              </div>
            </div>
          )}

          {/* Month Selector + MIS export — Phase 122: the sub-tab switcher,
              Expense Insights, Cash Flow / Revenue-by-Service charts and
              Most Expensive Project card all moved into the new Finance
              Ledger Analytics Panel (pages/Finance.jsx). */}
          <div className="flex items-center justify-end mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <MonthSelector value={financeMonth} onChange={setFinanceMonth} testId="dashboard-finance-month-selector" />
              <Button
                variant="outline"
                className="rounded-sm h-8 text-xs"
                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/finance/mis-export`, "_blank")}
                data-testid="dashboard-mis-export-btn"
              >
                <Download size={12} className="mr-1" /> Bulk export (MIS)
              </Button>
            </div>
          </div>

          {/* Three summary tiles (Month Selector filtered) */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
            <div className="card-flat p-4" data-testid="finance-tile-revenue">
              <div className="flex items-center justify-between mb-2">
                <span className="label-overline">Revenue</span>
                <TrendingUp size={13} className="text-slate-400" />
              </div>
              <div className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{INR(financeSummary.revenue)}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">net received ₹ (or amount × fx rate)</div>
            </div>
            <div className="card-flat p-4" data-testid="finance-tile-expenses">
              <div className="flex items-center justify-between mb-2">
                <span className="label-overline">Expenses</span>
                <Receipt size={13} className="text-slate-400" />
              </div>
              <div className="font-display text-2xl sm:text-3xl font-bold text-slate-900">{INR(financeSummary.expense)}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">sum of expense amounts</div>
            </div>
            <div className="card-flat p-4" data-testid="finance-tile-net">
              <div className="flex items-center justify-between mb-2">
                <span className="label-overline">Net P&amp;L</span>
                {financeSummary.net >= 0 ? <TrendingUp size={13} className="text-green-600" /> : <TrendingDown size={13} className="text-red-600" />}
              </div>
              <div className={`font-display text-2xl sm:text-3xl font-bold ${financeSummary.net >= 0 ? "text-green-700" : "text-red-700"}`}>
                {financeSummary.net < 0 ? "−" : ""}{INR(Math.abs(financeSummary.net))}
              </div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">{financeSummary.net >= 0 ? "profit" : "loss"} · revenue − expenses</div>
            </div>
          </div>

          {/* Snapshot tiles (NOT period-filtered) */}
          <div className="grid grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-4 mb-6">
            <TileLink to="/studio?tab=finance"           label="Outstanding invoices" value={outstandingInvoices.length} icon={FileText}      accent={outstandingInvoices.length > 0 ? "text-[#F54800]" : ""} testId="tile-outstanding-invoices" />
            <TileLink to="/studio?tab=purchases&status=pending" label="Purchase requests" value={pendingPurchases.length} icon={ShoppingCart} testId="tile-purchase-requests" />
          </div>

          {/* Invoice ageing — existing widget */}
          <div className="mb-4">
            <InvoiceAgeingWidget />
          </div>
        </>
      )}
    </TabSection>
  );
}
